// useHomeActionQueue — urgency-aware "What needs you" feed for the
// Home page Action Queue card. Composes prioritized items from
// existing real-data hooks; never invents data.
//
// Priority bands (sorted in this order, capped at 4 visible items):
//   1. urgent — action required from the user now
//        - active phantom cycle in 'pending' status → "Decide Week N stake"
//        - no active rollover chain at all → "Confirm your Phantom Week 1 placement"
//        - active chain but no Sika goal → "Tell Sika what you're saving for"
//   2. pos    — positive recent event (last 24h)
//        - recent settled cycle with payout > 0 → "Week N verified · GH₵N protected"
//   3. info   — soft watch / FYI
//        - active goal → "Sika check-in · on pace…"
//        - cashout-watch event in last 6h (from useHomeActivity) → "Cashout watch on …"
//        - any other recent audit_event/agent_log → fall through

import { useMemo } from "react";
import { usePhantomCycle } from "../../phantom/usePhantomCycle.ts";
import { usePhantomCycleHistory } from "../../phantom/usePhantomCycleHistory.ts";
import { useSikaGoals } from "../../sika/useSikaGoals.ts";
import { useRolloverChain } from "../../vault/useRolloverChain.ts";
import { useHomeActivity } from "./useHomeActivity.ts";

export type ActionQueueIcon = "bolt" | "info" | "eye" | "check";
export type ActionQueueTone = "urgent" | "info" | "pos";

export interface ActionQueueItem {
  id: string;
  type: ActionQueueTone;
  icon: ActionQueueIcon;
  title: string;
  body: string;
  /** Pre-formatted relative time string ("Just now", "2h ago", "Action needed", etc). */
  time: string;
  cta: string;
  href: string;
  /** When true, render the CTA in primary style. */
  primary: boolean;
  /** Internal sort tiebreak — newest events bubble up within a band. */
  ts: number;
}

export interface UseHomeActionQueueState {
  items: ActionQueueItem[];
  loading: boolean;
  error: string | null;
}

const BAND_RANK: Record<ActionQueueTone, number> = { urgent: 0, pos: 1, info: 2 };
const HOUR_MS = 3600 * 1000;
const SIX_HOURS_MS = 6 * HOUR_MS;
const TWENTY_FOUR_HOURS_MS = 24 * HOUR_MS;

function shortRelative(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return "Just now";
  if (diff < HOUR_MS) return `${String(Math.round(diff / 60_000))} min`;
  if (diff < TWENTY_FOUR_HOURS_MS) return `${String(Math.round(diff / HOUR_MS))}h ago`;
  return new Date(ts).toISOString().slice(0, 10);
}

function fmtCcy(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return `GH₵${Math.round(Number(n)).toLocaleString()}`;
}

export function useHomeActionQueue(limit: number = 4): UseHomeActionQueueState {
  const chainState = useRolloverChain();
  const cycleState = usePhantomCycle();
  const historyState = usePhantomCycleHistory();
  const goalsState = useSikaGoals();
  const activity = useHomeActivity(8);

  const items = useMemo<ActionQueueItem[]>(() => {
    const out: ActionQueueItem[] = [];
    const chain = chainState.chain;
    const cycle = cycleState.cycle;
    const goals = goalsState.goals;
    const activeGoal = goals.find((g) => g.status === "active") ?? goals[0] ?? null;
    const now = Date.now();

    // ─── URGENT band ─────────────────────────────────────────────
    if (chain && cycle && cycle.status === "pending") {
      const week = cycle.week_number ?? chain.current_week ?? 1;
      const stakeHint =
        cycle.stake_amount && cycle.stake_amount > 0
          ? ` · last week's stake ${fmtCcy(cycle.stake_amount)}`
          : "";
      out.push({
        id: `urgent:stake:${cycle.id}`,
        type: "urgent",
        icon: "bolt",
        title: `Decide your Week ${String(week)} stake`,
        body: `Open the Rollover surface to lock floor / recommended / full compound${stakeHint}.`,
        time: "Action needed",
        cta: "Decide stake",
        href: "/rollover",
        primary: true,
        ts: now,
      });
    }

    if (!chain && !chainState.loading) {
      out.push({
        id: "urgent:onboarding:phantom",
        type: "urgent",
        icon: "bolt",
        title: "Confirm your Phantom Week 1 placement",
        body: "Pretend-place this week's signal to start the chain. STRIKZA never places bets — you copy the slip manually.",
        time: "Onboarding",
        cta: "Open brief",
        href: "/brief",
        primary: true,
        ts: now,
      });
    }

    if (chain && !activeGoal && !goalsState.loading) {
      out.push({
        id: "urgent:sika:no-goal",
        type: "urgent",
        icon: "bolt",
        title: "Tell Sika what you're saving for",
        body: "A goal turns the chain into a weekly target you can track. Plain English works — \"buy a laptop\", \"school fees\".",
        time: "1 min",
        cta: "Talk to Sika",
        href: "/sika",
        primary: true,
        ts: now - 1,
      });
    }

    // ─── POSITIVE band ───────────────────────────────────────────
    const recentWin = historyState.cycles.find((c) => {
      const settled = c.updated_at ?? c.created_at ?? null;
      if (!settled) return false;
      const t = new Date(settled).getTime();
      if (Number.isNaN(t)) return false;
      if (now - t > TWENTY_FOUR_HOURS_MS) return false;
      const payout = Number(c.payout ?? 0);
      return c.result_type === "win" || payout > 0;
    });
    if (recentWin) {
      const ts = new Date(recentWin.updated_at ?? recentWin.created_at ?? now).getTime();
      const payout = Number(recentWin.payout ?? 0);
      const week = recentWin.week_number ?? "?";
      out.push({
        id: `pos:win:${recentWin.id}`,
        type: "pos",
        icon: "check",
        title: `Week ${String(week)} verified · ${fmtCcy(payout)} protected`,
        body: "Slip resolved. Decide whether to roll the win forward, redraw, or top up the investment seed.",
        time: shortRelative(ts),
        cta: "View",
        href: "/vault",
        primary: false,
        ts,
      });
    }

    // ─── INFO band ───────────────────────────────────────────────
    if (activeGoal) {
      const target = Number(activeGoal.target_amount ?? 0);
      const current = Number(activeGoal.current_amount ?? activeGoal.opening_stake ?? 0);
      const pct = target > 0 ? Math.round((current / target) * 100) : 0;
      out.push({
        id: `info:goal:${activeGoal.id}`,
        type: "info",
        icon: "info",
        title: "Sika check-in",
        body: `${activeGoal.title} · ${String(pct)}% of ${fmtCcy(target)} reached.`,
        time: "Today",
        cta: "Review goal",
        href: "/sika",
        primary: false,
        ts: new Date(activeGoal.updated_at ?? activeGoal.created_at ?? now).getTime(),
      });
    }

    // Pull cashout-watch / agent-log items from the activity feed.
    for (const it of activity.items) {
      if (out.length >= limit + 4) break; // soft cap before final sort
      const ts = it.timestamp ? new Date(it.timestamp).getTime() : now;
      if (Number.isNaN(ts)) continue;
      if (it.category === "cashout" && now - ts < SIX_HOURS_MS) {
        out.push({
          id: `info:cashout:${it.id}`,
          type: "info",
          icon: "eye",
          title: it.title,
          body: it.body,
          time: shortRelative(ts),
          cta: "Open",
          href: "/vault",
          primary: false,
          ts,
        });
      } else if (
        (it.category === "agent_log" || it.category === "audit_event") &&
        now - ts < TWENTY_FOUR_HOURS_MS
      ) {
        out.push({
          id: `info:event:${it.id}`,
          type: "info",
          icon: "info",
          title: it.title,
          body: it.body,
          time: shortRelative(ts),
          cta: "Review",
          href: "/audit",
          primary: false,
          ts,
        });
      }
    }

    // De-dup by id, sort band → recency, cap.
    const seen = new Set<string>();
    const deduped = out.filter((x) => (seen.has(x.id) ? false : (seen.add(x.id), true)));
    deduped.sort((a, b) => {
      const ba = BAND_RANK[a.type];
      const bb = BAND_RANK[b.type];
      if (ba !== bb) return ba - bb;
      return b.ts - a.ts;
    });
    return deduped.slice(0, limit);
  }, [
    chainState.chain,
    chainState.loading,
    cycleState.cycle,
    historyState.cycles,
    goalsState.goals,
    goalsState.loading,
    activity.items,
    limit,
  ]);

  const loading =
    chainState.loading ||
    cycleState.loading ||
    historyState.loading ||
    goalsState.loading ||
    activity.loading;

  const error =
    chainState.error ?? cycleState.error ?? historyState.error ?? goalsState.error ?? activity.error;

  return useMemo<UseHomeActionQueueState>(
    () => ({ items, loading, error }),
    [items, loading, error],
  );
}
